import PropTypes from "prop-types";
import { theme } from "../../../theme";
import styled from "styled-components";
import Button from "../../Button/Button";
import Switch from "../../Switch";
import Typography from "../../Typography";
import {
	BlockHeader,
	BlockHeaderContent,
	NearbySettings,
	NotificationSection,
	PrefsForm,
	SettingsBlock,
	SettingsLayout,
	SettingsLayoutVertical,
} from "./UserSettingsTabPrimitives";

function UserSettingsNotificationsTab({
	t,
	prefs,
	savingOptionKeys,
	onSectionAutosave,
	hasLocationCoordinates,
	onConfigureLocation,
}) {
	const sections = [
		{
			key: "events",
			label: t("settings.notifications.categories.events"),
			description: t("settings.notifications.descriptions.events"),
			options: [
				{
					key: "eventsInApp",
					label: t("settings.notifications.channels.inApp"),
					value: prefs.eventsInApp,
					onChange: (val) =>
						onSectionAutosave("events", "eventsInApp", { eventsInApp: val }),
				},
			],
		},
		{
			key: "recommendations",
			label: t("settings.notifications.categories.recommendations"),
			description: t("settings.notifications.descriptions.recommendations"),
			options: [
				{
					key: "recommendationsInApp",
					label: t("settings.notifications.channels.inApp"),
					value: prefs.recommendationsInApp,
					onChange: (val) =>
						onSectionAutosave("recommendations", "recommendationsInApp", {
							recommendationsInApp: val,
						}),
				},
			],
		},
		{
			key: "profile",
			label: t("settings.notifications.categories.profile"),
			description: t("settings.notifications.descriptions.profile"),
			options: [
				{
					key: "profileInApp",
					label: t("settings.notifications.channels.inApp"),
					value: prefs.profileInApp,
					onChange: (val) =>
						onSectionAutosave("profile", "profileInApp", { profileInApp: val }),
				},
			],
		},
		{
			key: "organizerEvents",
			label: t("settings.notifications.categories.organizerEvents"),
			description: t("settings.notifications.descriptions.organizerEvents"),
			options: [
				{
					key: "organizerInApp",
					label: t("settings.notifications.channels.inApp"),
					value: prefs.organizerInApp,
					onChange: (val) =>
						onSectionAutosave("organizerEvents", "organizerInApp", {
							organizerInApp: val,
						}),
				},
				{
					key: "organizerEmail",
					label: t("settings.notifications.channels.email"),
					value: prefs.organizerEmail,
					onChange: (val) =>
						onSectionAutosave("organizerEvents", "organizerEmail", {
							organizerEmail: val,
						}),
				},
			],
		},
	];

	return (
		<SettingsBlock>
			<BlockHeader>
				<BlockHeaderContent>
					<Typography
						as="h2"
						$variant="title-5-semibold"
						$variantMobile="body-1-medium"
						$variantTablet="body-1-medium"
					>
						{t("settings.notifications.title")}
					</Typography>
				</BlockHeaderContent>
			</BlockHeader>
			<PrefsForm>
				{sections.map((section) => {
					const isSectionSaving = section.options.some((option) =>
						savingOptionKeys.includes(option.key),
					);
					const showRecommendationsHelper =
						section.key === "recommendations" &&
						prefs.recommendationsInApp &&
						!hasLocationCoordinates;

					return (
						<NotificationSection key={section.key}>
							{showRecommendationsHelper ? (
								<SettingsLayoutVertical>
									<RecommendationsSectionLayout>
										<NotificationCopy>
											<Typography
												$variant="body-1-semibold"
												$variantMobile="body-2-medium"
												$variantTablet="body-2-medium"
											>
												{section.label}
											</Typography>
											<NotificationDescription
												$variant="body-2-medium"
												color={theme.colors.defaultWeak}
											>
												{section.description}
											</NotificationDescription>
										</NotificationCopy>
										<SectionAside aria-busy={isSectionSaving}>
											<OptionsCard>
												{section.options.map((option) => {
													const isOptionSaving = savingOptionKeys.includes(
														option.key,
													);

													return (
														<OptionRow
															key={option.key}
															$isSaving={isOptionSaving}
														>
															<Typography $variant="body-2-medium">
																{option.label}
															</Typography>
															<Switch
																value={option.value}
																disabled={isOptionSaving}
																ariaLabel={`${section.label} · ${option.label}`}
																onChange={option.onChange}
															/>
														</OptionRow>
													);
												})}
											</OptionsCard>
										</SectionAside>
									</RecommendationsSectionLayout>

									<RecommendationsHelperSlot>
										<NearbySettings>
											<Typography
												$variant="body-2-medium"
												color={theme.colors.defaultWeak}
											>
												{t("settings.notifications.locationHelper")}
											</Typography>
											<Button
												$variant="outline"
												size="m"
												onClick={onConfigureLocation}
												disabled={savingOptionKeys.includes(
													"recommendationsInApp",
												)}
											>
												{t("settings.notifications.configureLocation")}
											</Button>
										</NearbySettings>
									</RecommendationsHelperSlot>
								</SettingsLayoutVertical>
							) : (
								<SettingsLayout>
									<NotificationCopy>
										<Typography
											$variant="body-1-semibold"
											$variantMobile="body-2-medium"
											$variantTablet="body-2-medium"
										>
											{section.label}
										</Typography>
										<NotificationDescription
											$variant="body-2-medium"
											color={theme.colors.defaultWeak}
										>
											{section.description}
										</NotificationDescription>
									</NotificationCopy>
									<SectionAside aria-busy={isSectionSaving}>
										<OptionsCard>
											{section.options.map((option) => {
												const isOptionSaving = savingOptionKeys.includes(
													option.key,
												);

												return (
													<OptionRow
														key={option.key}
														$isSaving={isOptionSaving}
													>
														<Typography $variant="body-2-medium">
															{option.label}
														</Typography>
														<Switch
															value={option.value}
															disabled={isOptionSaving}
															ariaLabel={`${section.label} · ${option.label}`}
															onChange={option.onChange}
														/>
													</OptionRow>
												);
											})}
										</OptionsCard>
									</SectionAside>
								</SettingsLayout>
							)}
						</NotificationSection>
					);
				})}
			</PrefsForm>
		</SettingsBlock>
	);
}

const NotificationCopy = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: ${({ theme }) => theme.sizing.xxs};
	min-width: 0;
`;

const NotificationDescription = styled(Typography)`
	max-width: 640px;
`;

const RecommendationsSectionLayout = styled.div`
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	justify-content: space-between;
	gap: ${({ theme }) => theme.sizing.sm};	
`;

const RecommendationsHelperSlot = styled.div`

`;

const SectionAside = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	gap: ${({ theme }) => theme.sizing.sm};
	flex-shrink: 0;

	@media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
		align-self: flex-end;
	}
`;

const OptionsCard = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.sizing.xs};
	margin-left: auto;
	min-width: 240px;

	@media (max-width: ${({ theme }) => theme.breakpoints.md}) {
		width: 100%;
		min-width: 0;
	}
`;

const OptionRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: ${({ theme }) => theme.sizing.sm};
	opacity: ${({ $isSaving }) => ($isSaving ? 0.56 : 1)};
	transition: opacity 0.2s ease;
`;

UserSettingsNotificationsTab.propTypes = {
	t: PropTypes.func.isRequired,
	prefs: PropTypes.shape({
		eventsInApp: PropTypes.bool.isRequired,
		recommendationsInApp: PropTypes.bool.isRequired,
		profileInApp: PropTypes.bool.isRequired,
		organizerInApp: PropTypes.bool.isRequired,
		organizerEmail: PropTypes.bool.isRequired,
	}).isRequired,
	savingOptionKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
	onSectionAutosave: PropTypes.func.isRequired,
	hasLocationCoordinates: PropTypes.bool.isRequired,
	onConfigureLocation: PropTypes.func.isRequired,
};

export default UserSettingsNotificationsTab;
